/**
 * A {@link Map} subclass that automatically generates default values for unset
 * keys. If {@link Map#get} is called with a key that isn't present in the map,
 * the function passed to the constructor will be called to generate a value for
 * that key and the map will be updated to include the new key/value pair.
 */
export class DefaultMap<K, V> extends Map<K, V> {
  /**
   * @param _makeDefault - A function which will be used to generate default
   * values when a key is not present in the map. The key in question is
   * provided as a parameter so that the default value can be a function of the
   * key if desired.
   * @param entries - An initial set of entries for the map. See
   * {@link Map#constructor}.
   */
  constructor(private _makeDefault: (key: K) => V, entries?: readonly (readonly [K, V])[] | null) {
    super(entries);
  }

  get(key: K): V {
    let value = super.get(key);
    if (!value) {
      value = this._makeDefault(key);
      super.set(key, value);
    }
    return value;
  }
}

/**
 * Like DefaultMap but for WeakMap.
 */
export class DefaultWeakMap<K extends object, V> extends WeakMap<K, V> {
  /**
   * @param _makeDefault - A function which will be used to generate default
   * values when a key is not present in the map. The key in question is
   * provided as a parameter so that the default value can be a function of the
   * key if desired.
   * @param entries - An initial set of entries for the map. See
   * {@link Map#constructor}.
   */
  constructor(private _makeDefault: (key: K) => V, entries?: readonly [K, V][] | null) {
    super(entries);
  }

  get(key: K): V {
    let value = super.get(key);
    if (!value) {
      value = this._makeDefault(key);
      super.set(key, value);
    }
    return value;
  }
}
